import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, Subscription, switchMap, take, tap } from 'rxjs';
import { SrsReviewModalComponent } from '../srs-modals/srs-review-modal/srs-review-modal.component';
import { SrsFinaliseBallotModalComponent } from '../srs-modals/srs-finalise-ballot-modal/srs-finalise-ballot-modal.component';
import { appConstants } from '../../../../../shared/constants/app-constants';
import { UnifiedBallotStatus } from '../../../../../shared/enums/unified-ballot-status-enum';
import { IUser } from '../../../../../shared/models/user';
import { AuthService } from '../../../../../shared/services/auth.service';
import { SamlService } from '../../../../../shared/services/saml.service';
import { UserUtility } from '../../../../../shared/utilities/user-utility';
import { ISrsBallotDetail } from '../../../shared/models/sr/srs-ballot-details';
import { SrsBallotReviewOutcome } from '../../../shared/models/sr/srs-ballot-review-outcome';
import { SrsBallotReviewStage } from '../../../shared/models/sr/srs-ballot-review-stage';
import { SrsVoteId } from '../../../shared/models/sr/srs-vote';

@Component({
    selector: 'app-srs-ballot-review-actions',
    templateUrl: './srs-ballot-review-actions.component.html',
    styleUrl: './srs-ballot-review-actions.component.scss',
    standalone: false
})

export class SrsBallotReviewActionsComponent implements OnInit, OnDestroy {

  @Output() afterSave = new EventEmitter<string>();
  @Input() srsBallotDetail: ISrsBallotDetail | null = null;

  @Input() majorityVote: {
    label: string;
    value: number;
    color: string;
    icon: string;
    ballotId: string;
  } | null = null;
  @Input() isAllNoResponse: boolean = false;
  @Input() hasMetConsensus: boolean = false;

  subscriptions: Subscription = new Subscription();

  reviewAction = SrsBallotReviewOutcome;

  currentUser!: IUser;
  isInternalUser: boolean = false;
  isInternalPublicUser: boolean = false;
  isHoSD: boolean = false;

  showGenerateReportButton: boolean = false;
  showFinaliseButton: boolean = false;
  isFinalisedButtonEnabled: boolean = false;
  showApproveAndRejectButtons: boolean = false;
  showNotApplicableButton: boolean = false;
  showSnzReviewButton: boolean = false;

  constructor(
    private _authService: AuthService,
    private _samlService: SamlService,
    private _modalService: NgbModal
  ) {  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {

    const currentUserSubscription =
      this._authService.currentUserSubject
      .pipe(
        filter(user => !!user),
        tap((user) => {
          this.currentUser = user as IUser;
        }),
        switchMap(() => {
          return this._samlService.getLoggedInUserHoSDStatus();
        }),
        take(1)
      )
      .subscribe((hosdResponse) => {
        this.isHoSD = hosdResponse.content;
        this.setUserPermissions();
      });
    this.subscriptions.add(currentUserSubscription);
  }

  setUserPermissions(): void {
    if (!this.srsBallotDetail || !this.currentUser) {
      return;
    }

    this.isInternalUser = UserUtility.isInternalUser(this.currentUser);
    this.isInternalPublicUser = UserUtility.isInternalPublicUser(this.currentUser);


    const isSrt = UserUtility.hasRoles(this.currentUser, appConstants.userRole.moderator);
    const isPgm = UserUtility.hasRoles(this.currentUser, appConstants.userRole.programManager);
    this.showGenerateReportButton = this.isInternalUser &&
      (this.srsBallotDetail.unifiedStatusId == UnifiedBallotStatus.closedForVoting ||
       this.srsBallotDetail.unifiedStatusId == UnifiedBallotStatus.pendingFinalisedApproval ||
       this.srsBallotDetail.unifiedStatusId == UnifiedBallotStatus.reviewComplete);

    this.showFinaliseButton = this.srsBallotDetail.unifiedStatusId == UnifiedBallotStatus.closedForVoting && !this.isInternalPublicUser;

    this.isFinalisedButtonEnabled = UserUtility.userIsPmOrAbove(this.currentUser) &&
      this.srsBallotDetail.unifiedStatusId == UnifiedBallotStatus.closedForVoting &&
      (this.hasMetConsensus || this.isAllNoResponse);


    switch (this.srsBallotDetail.ballotResolution) {
      case SrsVoteId.SVT_REVISE:
        this.showApproveAndRejectButtons =
          ((isPgm || isSrt)&& this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.PgmReview);

        this.showNotApplicableButton = false;
        this.showSnzReviewButton = false;
        break;

      case SrsVoteId.SVT_WITHDRAW:
        this.showApproveAndRejectButtons =
          (isSrt && this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SrtReview) ||
          (isSrt && this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.RegulatorReview)

        this.showNotApplicableButton =
          isSrt && this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.RegulatorReview;

        this.showSnzReviewButton =
           isSrt  && this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SnzReview;
        break;

      case SrsVoteId.SVT_RECONFIRM:
        this.showApproveAndRejectButtons =
          (isSrt && this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SrtReview) ||
          (isPgm && this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SdApproverReview)

        this.showNotApplicableButton = false

        this.showSnzReviewButton =
           isSrt  && this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SnzReview;
        break;
      default:
        break;
    }

  }

  openFinaliseBallotModal() {
    const modalRef = this._modalService.open(SrsFinaliseBallotModalComponent);
    modalRef.componentInstance.majorityVote = this.majorityVote;
    modalRef.componentInstance.isAllNoResponse = this.isAllNoResponse;

    this.subscriptions.add(modalRef.closed.subscribe(
      {
        next: (result) => {

          this.afterSave.emit(result);
        }
      },
    ));
  }

  onButtonClicked(reviewOutcome: SrsBallotReviewOutcome): void {
    if (!this.srsBallotDetail) {
      return;
    }

    switch (this.srsBallotDetail.ballotResolution) {
      case SrsVoteId.SVT_REVISE:
        this.openReviseModalDialog(reviewOutcome);
        break;
      case SrsVoteId.SVT_WITHDRAW:
        this.openWithdrawModalDialog(reviewOutcome);
        break;
      case SrsVoteId.SVT_RECONFIRM:
        this.openReconfirmModalDialog(reviewOutcome);
        break;
      default:
        break;
    }
  }

  openReviseModalDialog(reviewOutcome: SrsBallotReviewOutcome): void {
    if (!this.srsBallotDetail?.reviewStageId) {
      return;
    }

    switch (this.srsBallotDetail.reviewStageId) {
      case SrsBallotReviewStage.PgmReview:
        const modalTitle = reviewOutcome === SrsBallotReviewOutcome.Approve ? 'Approve Decision' : 'Reject Decision';
        const modalMessage = '';
        const requireComments = reviewOutcome === SrsBallotReviewOutcome.Reject;

        this.openSrsReviewModalDialog(
          modalTitle,
          modalMessage,
          this.srsBallotDetail.ballotId,
          this.srsBallotDetail.ballotResolution,
          this.srsBallotDetail.reviewStageId,
          reviewOutcome,
          requireComments,
          false
        );
        break;
      default:
        console.log(`Invalid ReviewStage ${this.srsBallotDetail.reviewStageId} for Revise.`)
        break;
    }
  }

  openWithdrawModalDialog(reviewOutcome: SrsBallotReviewOutcome) : void {
    if (!this.srsBallotDetail?.reviewStageId) {
      return;
    }

    let modalTitle = '';
    let modalMessage = '';
    let requireComments = reviewOutcome === SrsBallotReviewOutcome.Reject;
    let isYesNoButtons = false;

    switch (this.srsBallotDetail.reviewStageId) {
      case SrsBallotReviewStage.SrtReview:
        modalTitle = reviewOutcome === SrsBallotReviewOutcome.Approve ? 'Approve Decision' : 'Reject Decision';
        modalMessage = '';

        break;

      case SrsBallotReviewStage.RegulatorReview:
        modalTitle = reviewOutcome === SrsBallotReviewOutcome.Approve ? 'Regulator Approves' :
          reviewOutcome === SrsBallotReviewOutcome.Reject ? 'Regulator Rejects' :
          reviewOutcome ===  SrsBallotReviewOutcome.NotApplicable ? 'Regulator Not Applicable' : '?';

        modalMessage = reviewOutcome === SrsBallotReviewOutcome.Reject ?
          'As the regulator has rejected the withdrawal of this publication, the status of this ballot goes back to Closed for Voting status for the PM to discuss with the Committee.  Outline in the comments field below the reason for the regulator\'s rejection.' :
          '';
        break;

      case SrsBallotReviewStage.SnzReview:
        modalTitle = "Has SNZ Finished Their Review?";
        modalMessage = 'Outline in the comments field below the outcome of the SNZ review.  If this differs from the AU position, ensure the TC has been advised and confirm whether this outcome should still be applied.  If so, follow the steps to proceed with the outcome as the document de-jointed.';
        requireComments = true;
        isYesNoButtons = true;
        break;

      default:
        console.log(`Invalid ReviewStage ${this.srsBallotDetail.reviewStageId} for Withdraw.`)
        break;
    }

    if (this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SrtReview
      || this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.RegulatorReview
      || this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SnzReview
    ) {
      this.openSrsReviewModalDialog(
        modalTitle,
        modalMessage,
        this.srsBallotDetail.ballotId,
        this.srsBallotDetail.ballotResolution,
        this.srsBallotDetail.reviewStageId,
        reviewOutcome,
        requireComments,
        isYesNoButtons
      );
    }
  }

  openReconfirmModalDialog(reviewOutcome: SrsBallotReviewOutcome): void {
    if (!this.srsBallotDetail?.reviewStageId) {
      return;
    }

    let modalTitle = '';
    let modalMessage = '';
    let requireComments = false;
    let isYesNoButtons = false;

    switch (this.srsBallotDetail.reviewStageId) {
      case SrsBallotReviewStage.SrtReview:
      case SrsBallotReviewStage.SdApproverReview:
        modalTitle = reviewOutcome === SrsBallotReviewOutcome.Approve ? 'Approve Decision' : 'Reject Decision';
        modalMessage = '';
        requireComments = reviewOutcome === SrsBallotReviewOutcome.Reject || this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SdApproverReview;

        break;
      case SrsBallotReviewStage.SnzReview:
        modalTitle = "Has SNZ Finished Their Review?";
        modalMessage = 'Outline in the comments field below the outcome of the SNZ review.  If this differs from the AU position, ensure the TC has been advised and confirm whether this outcome should still be applied.  If so, follow the steps to proceed with the outcome as the document de-jointed.';
        requireComments = true;
        isYesNoButtons = true;
        break;
      default:
        console.log(`Invalid ReviewStage ${this.srsBallotDetail.reviewStageId} for Reconfirm.`)
        break;
    }

    if (this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SnzReview
      || this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SdApproverReview
      || this.srsBallotDetail.reviewStageId === SrsBallotReviewStage.SrtReview
    ) {
      this.openSrsReviewModalDialog(
        modalTitle,
        modalMessage,
        this.srsBallotDetail.ballotId,
        this.srsBallotDetail.ballotResolution,
        this.srsBallotDetail.reviewStageId,
        reviewOutcome,
        requireComments,
        isYesNoButtons
      );
    }
  }

  openSrsReviewModalDialog(
    modalTitle: string,
    modalMessage: string,
    ballotId: string,
    resolutionId: string,
    reviewStageId: SrsBallotReviewStage | undefined,
    reviewOutcomeId: SrsBallotReviewOutcome,
    requireComments: boolean,
    isYesNoButtons: boolean
  ): void {
    const approvalModalRef = this._modalService.open(SrsReviewModalComponent);

    approvalModalRef.componentInstance.modalTitle = modalTitle;
    approvalModalRef.componentInstance.modalMessage = modalMessage;

    approvalModalRef.componentInstance.ballotId = ballotId;
    approvalModalRef.componentInstance.resolutionId = resolutionId;
    approvalModalRef.componentInstance.reviewStageId = reviewStageId;
    approvalModalRef.componentInstance.reviewOutcomeId = reviewOutcomeId;
    approvalModalRef.componentInstance.requireComments = requireComments;
    approvalModalRef.componentInstance.isYesNoButtons = isYesNoButtons;

    this.subscriptions.add(approvalModalRef.closed.subscribe({
      next: (result) => {
        //console.log('SrsReviewModal closed with result:', result);
        this.afterSave.emit(result);
      }
    }));
  }



}
